import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  Send as SendIcon,
  EmojiObjects as SuggestionIcon,
  TrendingUp as TrendingIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
    padding: theme.spacing(2),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  dialogContent: {
    padding: 0,
  },
  description: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  suggestionsList: {
    padding: 0,
    maxHeight: 300,
    overflowY: "auto",
  },
  suggestionItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  suggestionItemName: {
    fontWeight: 500,
  },
  suggestionItemDetails: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },
  suggestionItemPrice: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  reasonChip: {
    height: 18,
    fontSize: "0.65rem",
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between",
  },
  skipButton: {
    color: theme.palette.text.secondary,
  },
  sendButton: {
    minWidth: 160,
  },
  selectedCount: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

/**
 * Modal that appears before finalizing a sale to offer sending product suggestions
 * to the customer based on their purchase history
 */
const PdvSendSuggestionsModal = ({
  open,
  onClose,
  suggestions = [],
  customerName,
  onSend,
  onSkip,
  sending = false,
}) => {
  const classes = useStyles();
  const [selected, setSelected] = useState(new Set(suggestions.map((s) => s.id)));

  // Toggle selection of a product
  const handleToggle = useCallback((productId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  // Select/deselect all
  const handleToggleAll = useCallback(() => {
    if (selected.size === suggestions.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(suggestions.map((s) => s.id)));
    }
  }, [selected.size, suggestions]);

  // Handle send
  const handleSend = useCallback(() => {
    const selectedProducts = suggestions.filter((s) => selected.has(s.id));
    if (onSend) onSend(selectedProducts);
  }, [suggestions, selected, onSend]);

  // Handle skip
  const handleSkip = useCallback(() => {
    if (onSkip) onSkip();
    onClose();
  }, [onSkip, onClose]);

  const selectedCount = selected.size;
  const allSelected = selectedCount === suggestions.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <SuggestionIcon />
          <Typography variant="h6">Sugestoes de Venda</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} style={{ color: "inherit" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.description}>
          <Typography variant="body2">
            Deseja enviar estas sugestoes para{" "}
            <strong>{customerName || "o cliente"}</strong> antes de finalizar o
            pedido?
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
            Baseado no historico de compras, estes produtos podem interessar ao cliente.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              checked={allSelected}
              indeterminate={selectedCount > 0 && !allSelected}
              onChange={handleToggleAll}
              color="primary"
              size="small"
            />
            <Typography variant="body2">
              {allSelected ? "Desmarcar todos" : "Selecionar todos"}
            </Typography>
          </Box>
          <Typography className={classes.selectedCount}>
            {selectedCount} de {suggestions.length} selecionados
          </Typography>
        </Box>

        <List className={classes.suggestionsList}>
          {suggestions.map((product) => (
            <ListItem
              key={product.id}
              className={classes.suggestionItem}
              button
              onClick={() => handleToggle(product.id)}
            >
              <Checkbox
                checked={selected.has(product.id)}
                color="primary"
                size="small"
                style={{ marginRight: 8 }}
              />
              <ListItemText
                primary={
                  <Typography className={classes.suggestionItemName}>
                    {product.name}
                  </Typography>
                }
                secondary={
                  <Box className={classes.suggestionItemDetails}>
                    <span className={classes.suggestionItemPrice}>
                      {formatCurrency(product.price)}
                    </span>
                    {product.reason && (
                      <Chip
                        size="small"
                        icon={<TrendingIcon style={{ fontSize: 12 }} />}
                        label={product.reason}
                        className={classes.reasonChip}
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleSkip} className={classes.skipButton} disabled={sending}>
          Pular e finalizar
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || selectedCount === 0}
          className={classes.sendButton}
        >
          {sending ? "Enviando..." : `Enviar ${selectedCount > 0 ? `(${selectedCount})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdvSendSuggestionsModal;
